<template>
  <teleport to="body">
    <Transition
      enter-active-class="ease-in-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="visible"
        class="fixed inset-0 z-30 h-100vh w-100vw flex justify-center bg-transparent-70"
      >
        <FunctionalPanel
          :default-vh="80"
          is-resizable
          panel-key="choose-product"
          scroll-bar="vertical"
          :visible="visible"
          @update:visible="
            (e) => {
              visible = e
              reset()
            }
          "
        >
          <template #header>
            <div class="z-1 w-full border-b-1px border-green-transparent-10">
              <div class="pb-6px pt-10px title-12-h16-m text-primary-100">
                <p v-if="stackMenu.length === 0" class="text-center">
                  {{ $t('panel.title') }}
                </p>
                <div
                  v-else
                  class="flex cursor-pointer items-center gap-12px -mx-12px"
                  @click="stackMenu.pop()"
                >
                  <i class="icon-chevron-left text-24px text-primary-80" />
                  <p>
                    {{ lastStack?.title }}
                  </p>
                </div>
              </div>
              <PromoteMessageBar class="-mx-24px" />
              <!-- hint for grip -->
              <div
                v-if="
                  isProductItem(lastStack) &&
                  productState?.categoryType === UnboundDeviceCategoryType.Grip
                "
                class="flex items-center gap-8px bg-light-pink px-16px py-8px text-deep-brown -mx-24px"
              >
                <i class="icon-info-circle text-24px" />
                <p class="content-12-h16-r">{{ $t('message.compatibility') }}</p>
                <p class="lnk-12-h16-r" @click="isGripHintModalOpen = true">
                  {{ $t('message.learn_more') }}
                </p>
              </div>
            </div>
          </template>
          <template #content>
            <LoadingSpinner
              v-if="isCatalogLoading"
              class="mx-auto my-100px"
              is-loading
              :size="50"
            />
            <template v-else>
              <TransitionGroup
                enter-active-class="ease-in-out duration-200 transform delay-200"
                :enter-from-class="animationClass.enter"
                enter-to-class="translate-x-0"
                leave-active-class="ease-in duration-200"
                leave-from-class="translate-x-0"
                :leave-to-class="animationClass.leave"
              >
                <template v-if="isRootMenu">
                  <div
                    v-for="(category, categoryIndex) in catalog"
                    :key="`category${categoryIndex}`"
                  >
                    <div
                      v-if="!boundDesignGroupType"
                      class="flex cursor-pointer items-center border-b-1px border-light-gray px-16px py-12px"
                      @click="handleCategoryChange(getCategoryType(category))"
                    >
                      <i
                        class="icon-confirm text-20px text-brand-deep-yellow"
                        :class="{
                          'opacity-0': getCategoryType(category) !== productState?.categoryType,
                        }"
                      />
                      <img
                        :alt="getCategoryType(category)"
                        class="h-56px w-56px"
                        :src="category.thumbnailUrl"
                      />
                      <p class="content-12-h16-r text-primary-100">
                        {{ $t(`catalog.${getCategoryType(category)}.title`) }}
                      </p>
                      <i
                        class="icon-chevron-right ml-auto text-20px text-primary-60 duration-200"
                        :class="{ 'rotate-90': expandedCategoryType === getCategoryType(category) }"
                      />
                    </div>
                    <TransitionGroup
                      enter-active-class="ease-in duration-200"
                      enter-from-class="opacity-0 max-h-0"
                      enter-to-class="opacity-100 max-h-screen"
                      leave-active-class="ease-out duration-200"
                      leave-from-class="opacity-100 max-h-screen"
                      leave-to-class="opacity-0 max-h-0"
                    >
                      <div v-if="getCategoryType(category) === expandedCategoryType">
                        <div
                          v-for="(item, index) in categoryMenu"
                          :key="index"
                          class="flex items-center justify-between bg-light-gray py-22px"
                          :class="[
                            categoryMenu.find((e) => e.data === null)
                              ? 'pointer-events-none cursor-default'
                              : 'cursor-pointer',
                            boundDesignGroupType ? 'pl-40px pr-22px' : 'pl-96px pr-16px',
                          ]"
                          @click="handleCategoryMenuClick(item)"
                        >
                          <p class="content-12-h16-r text-primary-100">
                            {{ item.title }}
                          </p>
                          <div v-if="item.data !== null" class="flex items-center gap-8px">
                            <template v-if="item.type === 'color'">
                              <div
                                v-if="item.data.stockStatus"
                                class="tag"
                                :class="getStockStatusTagClasses(item.data.stockStatus)"
                              >
                                {{ $t(`stock-status.${item.data.stockStatus}`) }}
                              </div>
                              <AuColorDisplay
                                v-if="item.type === 'color'"
                                :color-type="item.data.colorType"
                                :value="item.data.value"
                              />
                            </template>
                            <p
                              class="whitespace-pre-wrap text-right content-11-h16-r text-primary-70"
                            >
                              {{ item.data.name }}
                            </p>
                            <i
                              class="icon-chevron-right text-20px text-primary-60"
                              :class="{ 'opacity-0': item.isSingleOption }"
                            />
                          </div>
                          <LoadingSpinner v-else is-loading />
                        </div>
                      </div>
                    </TransitionGroup>
                  </div>
                </template>
                <template v-else>
                  <template v-if="isProductItem(lastStack)">
                    <div
                      v-for="(groupItem, productSetGroupIndex) in productItemWithDisabled"
                      :key="`productSetGroup${productSetGroupIndex}`"
                      class="flex flex-col border-b-1px border-light-gray"
                    >
                      <div
                        class="flex items-center px-16px py-12px content-12-h16-r text-primary-100"
                        :class="{ 'cursor-pointer': !groupItem.isDisabled }"
                        @click="handleProductItemExpand(groupItem.productSetGroup)"
                      >
                        <i
                          class="icon-confirm text-20px text-brand-deep-yellow"
                          :class="{
                            'opacity-0':
                              groupItem.productSetGroup.groupKey !==
                                productState?.selectedProduct?.productSetGroup.groupKey ||
                              isProductSetGroupExpand(groupItem.productSetGroup),
                          }"
                        />
                        <img
                          :alt="groupItem.thumbnail"
                          class="ml-10px h-40px w-40px"
                          :class="{
                            'opacity-30': groupItem.isDisabled,
                          }"
                          :src="groupItem.thumbnail"
                        />
                        <p class="ml-8px">
                          <span
                            :class="{
                              'opacity-30': groupItem.isDisabled,
                            }"
                          >
                            {{ $t(`series.${groupItem.productSetGroup.groupKey}`) }}
                          </span>
                          <span
                            v-if="groupItem.isDisabled"
                            class="tag bg-primary-10 text-primary-50"
                            >{{ $t('tag.un_support') }}</span
                          >
                          <span
                            v-else-if="isSingleProduct(groupItem.productSetGroup)"
                            class="tag bg-primary-10 text-primary-50"
                            >{{
                              $t(
                                `version.${groupItem.productSetGroup.productSets[0]?.productOption[0]?.version}`,
                              )
                            }}</span
                          >
                        </p>
                        <i
                          v-if="
                            !isSingleProduct(groupItem.productSetGroup) && !groupItem.isDisabled
                          "
                          class="icon-chevron-up ml-auto text-20px text-primary-60 duration-200"
                          :class="{
                            'rotate-180': expandedProductSet !== groupItem.productSetGroup.groupKey,
                          }"
                        />
                      </div>
                      <TransitionGroup
                        enter-active-class="ease-in duration-200"
                        enter-from-class="opacity-0 max-h-0"
                        enter-to-class="opacity-100 max-h-screen"
                        leave-active-class="ease-out duration-200"
                        leave-from-class="opacity-100 max-h-screen"
                        leave-to-class="opacity-0 max-h-0"
                      >
                        <template
                          v-if="
                            isProductSetGroupExpand(groupItem.productSetGroup) &&
                            groupItem.productSetGroup.productSets
                          "
                        >
                          <div
                            v-for="(productSet, productSetIndex) in groupItem.productSetGroup
                              .productSets"
                            :key="productSetIndex"
                            class="bg-light-gray"
                          >
                            <p
                              v-if="groupItem.productSetGroup.productSets.length > 1"
                              class="py-8px pl-92px pr-16px content-11-h16-r text-primary-50"
                            >
                              {{ $t(`product-set.${productSet.handle}`) }}
                            </p>
                            <div
                              v-for="(product, productIndex) in productSet.productOption"
                              :key="productIndex"
                              class="flex cursor-pointer items-center gap-72px px-16px py-18px"
                              @click="
                                handleProductChange(product.handle, productSet.designGroupType)
                              "
                            >
                              <i
                                class="icon-confirm text-20px text-brand-deep-yellow"
                                :class="{
                                  'opacity-0':
                                    product!.handle !==
                                    productState?.selectedProduct?.product.handle,
                                }"
                              />
                              <p class="content-12-h16-r text-primary-100">
                                {{ $t(`version.${product!.version}`) }}
                              </p>
                            </div>
                          </div>
                        </template>
                      </TransitionGroup>
                    </div>
                  </template>
                  <template v-else>
                    <div
                      v-for="(item, index) in lastStack?.options"
                      :key="index + item.name"
                      class="flex items-center justify-between border-b-1px border-light-gray px-16px py-22px title-12-h16-m text-primary-100"
                      :class="
                        'stockStatus' in item && item.stockStatus === StockStatus.Pending
                          ? 'pointer-events-none cursor-default'
                          : 'cursor-pointer'
                      "
                      @click="lastStack?.onClick?.(item.value)"
                    >
                      <div class="flex items-center">
                        <i
                          class="icon-confirm mr-12px text-20px text-brand-deep-yellow"
                          :class="{ 'opacity-0': !isSelectedOption(item.value) }"
                        />
                        <AuColorDisplay
                          v-if="isColorItem(lastStack) && 'colorType' in item"
                          class="mr-12px"
                          :class="{
                            'opacity-30':
                              'stockStatus' in item && item.stockStatus === StockStatus.Pending,
                          }"
                          :color-type="item.colorType"
                          :value="item.colorDisplay"
                        />
                        <p
                          class="text-primary-100"
                          :class="{
                            'opacity-30':
                              'stockStatus' in item && item.stockStatus === StockStatus.Pending,
                          }"
                        >
                          {{ item.name }}
                        </p>
                        <template v-if="'stockStatus' in item">
                          <div class="tag" :class="getStockStatusTagClasses(item.stockStatus)">
                            {{ $t(`stock-status.${item.stockStatus}`) }}
                          </div>
                        </template>
                      </div>
                      <i
                        v-if="lastStack?.type === 'expandableItem'"
                        class="icon-chevron-right text-20px text-primary-60"
                      />
                    </div>
                  </template>
                </template>
              </TransitionGroup>
            </template>
          </template>
        </FunctionalPanel>
      </div>
    </Transition>
  </teleport>
  <ModalDialog v-model:visible="showConfirmDialog">
    <template #content>
      <p class="whitespace-pre">
        {{ $t('dialog.content.change_product') }}
      </p>
    </template>
    <template #footer>
      <AuButton @click="showConfirmDialog = false">{{ $t('btn.back') }}</AuButton>
      <AuButton type="primary-fill" @click="onConfirm?.()">{{ $t('btn.change_product') }}</AuButton>
    </template>
  </ModalDialog>
  <ModalDialog v-model:visible="isGripHintModalOpen">
    <template #header>{{ $t('dialog.title.grip') }}</template>
    <template #content>{{ $t('dialog.content.grip') }}</template>
    <template #footer
      ><AuButton type="primary-fill" @click="isGripHintModalOpen = false">{{
        $t('btn.ok')
      }}</AuButton></template
    >
  </ModalDialog>
</template>

<script setup lang="ts">
import { useDesignStore } from '@evolutivelabs/amuse-design-canvas'
import { AuButton, AuColorDisplay } from '@evolutivelabs/amuse-ui'
import { t } from 'i18next'
import { storeToRefs } from 'pinia'
import { computed, type DeepReadonly, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import FunctionalPanel from '@/components/FunctionalPanel.vue'
import PromoteMessageBar from '@/components/layout/PromoteMessageBar.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ModalDialog from '@/components/ModalDialog.vue'
import {
  type CategoryProperty,
  isBoundDeviceCategory,
  isBoundDeviceState,
  isColorItem,
  isProductItem,
  type OnOpenForProductPage,
  type ProductSetGroup,
} from '@/components/newProductPanel/newProductPanel.type'
import {
  getStockStatusTagClasses,
  isSingleProduct,
  useProductSelector,
} from '@/components/newProductPanel/useProductSelector'
import {
  type BoundDeviceCategoryType,
  type DesignGroupType,
  StockStatus,
  UnboundDeviceCategoryType,
} from '@/graphql/creator-platform/generated'
import { Route } from '@/router/route'
import { useNewProductStore } from '@/store/newProductStore'
import { type CategoryFragment } from '@/store/newProductStore.type'

const {
  stackMenu,
  expandedProductSet,
  changeProduct,
  selectProductSetGroup,
  selectVariant,
  selectDeviceColor,
  selectBrand,
} = useProductSelector(reset)

const visible = defineModel<boolean>('visible', { required: true })
const showConfirmDialog = ref(false)
let onConfirm: (() => void) | null = null
const newProductStore = useNewProductStore()
const designStore = useDesignStore()
const route = useRoute()
const { catalog, productState } = storeToRefs(useNewProductStore())
const expandedCategoryType = ref<BoundDeviceCategoryType | null | UnboundDeviceCategoryType>(null)
const animationClass = ref({ enter: '', leave: '' })
const isRootMenu = computed(() => stackMenu.value.length === 0)
const props = withDefaults(
  defineProps<{
    boundDesignGroupType?: DesignGroupType | null
    onOpen?: null | OnOpenForProductPage
  }>(),
  {
    boundDesignGroupType: null,
    onOpen: null,
  },
)
const isGripHintModalOpen = ref(false)

watch(
  () => stackMenu.value.length,
  (newValue, oldValue) => {
    animationClass.value =
      newValue > oldValue
        ? { enter: 'translate-x-full', leave: '-translate-x-full' }
        : { enter: '-translate-x-full', leave: 'translate-x-full' }
  },
)

const isCatalogLoading = computed(() => newProductStore.catalog === null)

onMounted(async () => {
  await newProductStore.init()
  if (productState.value !== null) {
    expandedCategoryType.value = productState.value.categoryType
  }
})

watch(
  () => visible.value,
  (newValue) => {
    if (newValue && props.onOpen !== null) {
      handleOpenFunctionInProductPage(props.onOpen)
    }
  },
)

function handleOpenFunctionInProductPage(onOpen: string): void {
  switch (onOpen) {
    case 'selectBrand': {
      selectBrand()
      break
    }
    case 'selectDeviceColor': {
      selectDeviceColor()
      break
    }
    case 'selectProductSetGroup': {
      selectProductSetGroup()
      break
    }
    case 'selectVariant': {
      selectVariant()
      break
    }
    default: {
      throw new Error('onOpen func not found')
    }
  }
}

function reset(): void {
  stackMenu.value = []
  expandedProductSet.value = null
}

function getCategoryType(
  e: DeepReadonly<CategoryFragment>,
): BoundDeviceCategoryType | UnboundDeviceCategoryType {
  return e.__typename === 'UnboundDeviceCategoryDto' ? e.unboundDeviceType : e.boundDeviceType
}

async function changeCategoryType(
  type: BoundDeviceCategoryType | UnboundDeviceCategoryType,
): Promise<void> {
  if (type === productState.value?.categoryType) {
    expandedCategoryType.value = expandedCategoryType.value === null ? type : null
  } else {
    expandedCategoryType.value = type
    try {
      await newProductStore.changeCategoryType(type)
    } catch (error) {
      window.alert((error as Error).message)
    }
  }
}

const categoryMenu = computed((): CategoryProperty[] => {
  const state = productState.value
  if (state === null) return []
  const product = state.selectedProduct
  const result: CategoryProperty[] = [
    {
      onClick: selectProductSetGroup,
      title: t(`catalog.${state.categoryType}.product`),
      data:
        product === null
          ? null
          : {
              name: `${product.title.product}\n${product.title.version}`,
            },
      type: 'text',
      isSingleOption: product?.productSetGroups.length === 1,
    },
    {
      onClick: selectVariant,
      title: t(`catalog.${state.categoryType}.product-color`),
      data:
        product === null
          ? null
          : {
              colorType: product.variant.color.type,
              name: product.variant.color.name,
              value: product.variant.color.value,
              stockStatus: product.variant.stockStatus,
            },
      type: 'color',
      isSingleOption: product?.product.variants.length === 1,
    },
  ]
  if (isBoundDeviceState(state)) {
    if (state.color !== null)
      result.unshift({
        onClick: selectDeviceColor,
        title: t(`catalog.${state.categoryType}.device-color`),
        data: { name: state.color.name, colorType: state.color.type, value: state.color.value },
        type: 'color',
        isSingleOption: state.device.colors.length === 1,
      })
    const selectedCatalog = catalog.value
      ?.filter((e) => isBoundDeviceCategory(e))
      .find((e) => e.boundDeviceType === state.categoryType)
    result.unshift({
      onClick: selectBrand,
      title: t(`catalog.${state.categoryType}.device`),
      data: {
        name: state.device.name,
      },
      type: 'text',
      isSingleOption:
        selectedCatalog?.brands.length === 1 && selectedCatalog.brands[0]?.devices.length === 1,
    })
  }
  return result
})

function handleCategoryMenuClick(item: CategoryProperty): void {
  if (item.isSingleOption) return
  void item.onClick?.()
}

const lastStack = computed(() => {
  return stackMenu.value[stackMenu.value.length - 1]
})

function isProductSetGroupExpand(productSetGroup: ProductSetGroup): boolean {
  return expandedProductSet.value === productSetGroup.groupKey && !isSingleProduct(productSetGroup)
}

function isSelectedOption(e: string): boolean | undefined {
  if (productState.value === null) return undefined
  return isBoundDeviceState(productState.value)
    ? [
        productState.value.brandName,
        productState.value.color?.handle,
        productState.value.device.handle,
        productState.value.selectedProduct?.productSetGroup.groupKey,
        productState.value.selectedProduct?.variant.sku,
      ].includes(e)
    : [
        productState.value.selectedProduct?.productSetGroup.groupKey,
        productState.value.selectedProduct?.variant.sku,
      ].includes(e)
}

function handleProductItemExpand(productSetGroup: ProductSetGroup): void {
  if (!isProductSetSelectable(productSetGroup)) return
  if (productSetGroup.productSets.length === 0) return
  if (isSingleProduct(productSetGroup)) {
    const handle = productSetGroup.productSets[0]?.productOption[0]?.handle
    const type = productSetGroup.productSets[0]?.designGroupType
    if (handle === undefined || type === undefined) return
    handleProductChange(handle, type)
  } else {
    expandProductSet(productSetGroup.groupKey)
  }
}

function isProductSetSelectable(productSetGroup: ProductSetGroup): boolean {
  if (props.boundDesignGroupType === null) return true
  const type = productSetGroup.productSets[0]?.designGroupType
  if (type === undefined) return true
  return type === props.boundDesignGroupType
}

function expandProductSet(groupKey: string): void {
  if (groupKey === expandedProductSet.value) {
    expandedProductSet.value = null
    return
  }
  expandedProductSet.value = groupKey
}

function handleCategoryChange(type: BoundDeviceCategoryType | UnboundDeviceCategoryType): void {
  if (type !== productState.value?.categoryType && shouldCheckProductChange.value) {
    showConfirmDialog.value = true
    onConfirm = () => {
      designStore.clear()
      void changeCategoryType(type)
      showConfirmDialog.value = false
    }
    return
  }
  void changeCategoryType(type)
}

function handleProductChange(handle: string, type: DesignGroupType): void {
  const currentDesignGroup = productState.value?.selectedProduct?.productSet.boundDesignGroup.type
  if (currentDesignGroup !== type && shouldCheckProductChange.value) {
    showConfirmDialog.value = true
    onConfirm = () => {
      designStore.clear()
      changeProduct(handle)
      showConfirmDialog.value = false
    }
    return
  }
  changeProduct(handle)
}

const shouldCheckProductChange = computed(
  () =>
    !designStore.design.isEmpty &&
    (route.name === Route.DesignPage ||
      route.name === Route.DesignArtistPage ||
      route.name === Route.DevelopPage),
)

const productItemWithDisabled = computed(() => {
  if (!isProductItem(lastStack.value)) return []
  return lastStack.value.productSetGroups.map((e) => {
    return {
      ...e,
      isDisabled:
        e.productSetGroup.productSets.length === 0 || !isProductSetSelectable(e.productSetGroup),
    }
  })
})
</script>

<style scoped>
.tag {
  @apply ml-8px rounded-full px-8px py-4px content-10-h12-r;
}
</style>
